// lib/leasing/storeQuery.ts
// Name normalisation shared by the store matchers: accents folded, punctuation dropped, spacing
// collapsed, so "Gémo" and "GEMO" and "gemo " compare equal. Extracted because the store-card
// enricher and the on-the-spot verifier must agree on what "same brand" means, two slightly
// different normalisers is how one of them confirms a store the other rejects.
export const normalizeStoreQuery = (value: string | null | undefined): string =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Words that describe what a place IS, not who runs it.
 *
 * "Galerie Woolworth" contains "Woolworth" and a substring match happily accepted it as a
 * Woolworth store. It is an art gallery in the 6th; the retailer has no French shops at all. That
 * is how OpenStreetMap's rootname scraping poured unrelated businesses into a brand's footprint,
 * and it is worst for brands nobody recognises, where there is no instinct to catch it.
 */
const PLACE_TYPE_WORDS = new Set([
  'galerie', 'gallery', 'restaurant', 'cafe', 'bar', 'brasserie', 'hotel', 'residence', 'clinique',
  'clinic', 'pharmacie', 'garage', 'agence', 'agency', 'ecole', 'school', 'salon', 'atelier',
  'studio', 'boulangerie', 'librairie', 'bureau', 'cabinet', 'association', 'fondation', 'musee',
  'museum', 'theatre', 'cinema', 'parking', 'station', 'centre', 'center', 'immobilier',
]);

/**
 * Is this place name the SAME retail brand, rather than something merely containing its name?
 *
 * Exact match always passes. Otherwise the extra words decide: a place whose name adds a
 * business-type word ("Galerie X", "Restaurant X") is a different kind of establishment and is
 * rejected, however well the brand token matches. Short brand names are held to exact match,
 * because a two or three letter token inside a longer name is coincidence more often than not.
 */
export const isSameBrandPlace = (brandName: string, placeName: string): boolean => {
  const brand = normalizeStoreQuery(brandName);
  // A branch's own label follows a dash: "La Brigade - Restaurant Brest", "Quick - Les Ulis".
  // Only the part before it names the business; read whole, "restaurant" rejected 17 of La
  // Brigade's 20 shops (2026-10-06). "Galerie Woolworth" has no dash and is still refused.
  const place = normalizeStoreQuery(placeName.split(/\s+[-–\u2014|]\s+/)[0]);
  if (!brand || !place) return false;
  if (brand === place) return true;
  // A short brand inside a longer name is usually a collision, not a branch.
  if (brand.length < 5) return false;
  if (!place.includes(brand) && !brand.includes(place)) return false;

  const extra = place
    .split(' ')
    .filter((word) => word && !brand.split(' ').includes(word));
  return !extra.some((word) => PLACE_TYPE_WORDS.has(word));
};

const GENERIC = new Set(['super', 'maison', 'cafe', 'restaurant', 'hotel', 'boulangerie', 'pharmacie', 'optique', 'centre', 'grand', 'petit', 'atelier', 'comptoir', 'france', 'paris', 'bistro', 'pizza', 'sushi', 'burger', 'marche', 'market', 'city', 'express', 'beauty', 'studio', 'house', 'store', 'shop', 'boutique', 'salon', 'institut', 'espace', 'galerie']);
const words = (name: string) => name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean).map((w) => w.replace(/s$/, ''));
/** Google's own spellings of a brand, read leniently (isSameBrandPlace is the strict reading): "CARTERCASH" (Carter-Cash), "GEMO AUBERGENVILLE - ..." (Gémo),
 *  "Cinéma CGR Le Colisée" (CGR Cinémas). Read strictly, they closed 1,662 open shops (2026-10-12). */
export const namesBrand = (fullBrand: string, place: string) => {
  // "MOMEN'TEA - BUBBLE TEA SHOP": the brand is the part before the dash, as for a place.
  const brand = fullBrand.split(/\s+[-–\u2014|]\s+/)[0] ?? fullBrand;
  if (isSameBrandPlace(brand, place)) return true;
  const b = words(brand), p = words(place);
  if (b.length && b.every((w) => p.includes(w))) return true;
  // "KIKO" for Kiko Milano: the brand's own first word, unless it is a common one (Super U, Maison du Monde).
  if (b[0] && b[0].length >= 4 && !GENERIC.has(b[0]) && p.includes(b[0])) return true;
  const joined = b.join('');
  return p.some((_, i) => p.slice(i).some((__, j) => p.slice(i, i + j + 1).join('') === joined));
};
